/**
 * @file `enemy_ranged_01`: the ranged-enemy template's behaviour.
 *
 * One system drives every instance of the template. All of the variation lives
 * in {@link ../config/EnemyRanged.js} — the parameters an instance is built
 * with — and in {@link ../components/RangedAI.js}, which is the *only* place
 * per-agent state is kept. Nothing in this file is stored per entity, so a
 * hundred simultaneous instances each keep their own state, destination,
 * target, cooldowns and counters.
 *
 * Machine:
 *
 *   Idle_Static ─┐
 *   Idle_Roaming ─┼─ detection / damage ─→ Aggro_Ranged ─ memory expired ─→ idle
 *   Idle_Patrol ─┘
 *
 * Combat inside `Aggro_Ranged` is a second, smaller table: the combat patterns.
 * Adding a pattern means adding one entry to `_patterns` plus one name to the
 * template enum — no existing pattern changes.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import {
  Transform,
  Velocity,
  Health,
  Weapon,
  PlayerControlled,
  RangedAI
} from '../components/index.js';
import {
  RangedState,
  CombatPattern,
  pickCombatPattern
} from '../config/EnemyRanged.js';
import { navGridForArena, PATHS_PER_FRAME } from '../ai/sharedNav.js';
import { Events } from '../core/EventBus.js';
import { PALETTE } from '../config/Palette.js';
import { isDevBuild } from '../utils/Dev.js';
import { angleDelta, dampAngle, distanceSqXZ, toRadians, TAU } from '../utils/MathUtils.js';

/** How far off-aim an agent may be and still pull the trigger, in radians. */
const AIM_TOLERANCE = 0.14;
/** Attempts made when sampling an attack position around the target. */
const REPOSITION_ATTEMPTS = 12;
/** Attempts made when sampling a cover position. */
const COVER_ATTEMPTS = 12;
/** Offsets tried around a cover point when looking for a firing position. */
const PEEK_OFFSETS = 8;
/** Distance from cover to the position it shoots from. */
const PEEK_DISTANCE = 1.8;
/** Arc segments used when drawing a vision cone. */
const DEBUG_CONE_SEGMENTS = 18;

/** Debug cone colours: neutral / in-cone-no-LOS / detected. */
const DEBUG_COLOR_NEUTRAL = 0x4aa3ff;
const DEBUG_COLOR_WARNING = 0xffcc14;
const DEBUG_COLOR_DETECTED = 0xff3b5c;

export class EnemyRangedAISystem extends System {
  // Runs just after the melee FSM, before combat reads triggers.
  static priority = 21;

  constructor() {
    super();
    /** @type {import('../ai/NavGrid.js').NavGrid | null} Shared arena grid. @private */
    this._nav = null;
    /** @type {number} Remaining A* runs this frame. @private */
    this._pathBudget = 0;
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
    /** @type {boolean} Vision-cone / AI debug overlay. @private */
    this._debugVisible = false;
    /** @type {Map<number, THREE.Line>} Per-entity debug meshes, keyed by entity id. @private */
    this._debugMeshes = new Map();
    /** @type {THREE.Vector3} Scratch for stun-star emission. @private */
    this._starPosition = new THREE.Vector3();
    /** @type {THREE.Vector3} Scratch for stun-star emission. @private */
    this._starDirection = new THREE.Vector3();
    /** @type {object} Reused FX payload. @private */
    this._starEvent = {
      position: this._starPosition,
      direction: this._starDirection,
      count: 1,
      options: {
        speed: [1.8, 2.2],
        spread: 0.08,
        life: [0.34, 0.42],
        size: [0.09, 0.15],
        color: PALETTE.stunStar
      }
    };

    /**
     * Reused context handed to every state and pattern handler, so the hot loop
     * allocates nothing.
     * @type {AgentContext}
     * @private
     */
    this._agent = {
      entity: null,
      ai: null,
      config: null,
      transform: null,
      velocity: null,
      player: null,
      playerTransform: null,
      distance: Infinity,
      dt: 0
    };

    /**
     * State table. Each entry only decides what the agent *does*; transitions
     * go through {@link EnemyRangedAISystem#_changeState}.
     * @type {Record<string, (agent: AgentContext) => void>}
     * @private
     */
    this._states = {
      [RangedState.IDLE_STATIC]: (agent) => this._updateIdleStatic(agent),
      [RangedState.IDLE_ROAMING]: (agent) => this._updateIdleRoaming(agent),
      [RangedState.IDLE_PATROL]: (agent) => this._updateIdlePatrol(agent),
      [RangedState.AGGRO_RANGED]: (agent) => this._updateAggro(agent)
    };

    /**
     * Combat patterns. Each one is responsible for movement only; whether the
     * trigger is pulled is decided afterwards, so no pattern can accidentally
     * shoot through a wall.
     * @type {Record<string, (agent: AgentContext) => void>}
     * @private
     */
    this._patterns = {
      [CombatPattern.STATIC_SHOOTER]: (agent) => this._patternStatic(agent),
      [CombatPattern.MOVING_SHOOTER]: (agent) => this._patternMoving(agent),
      [CombatPattern.STRAFE_SHOOTER]: (agent) => this._patternStrafe(agent),
      [CombatPattern.COVER_SHOOTER]: (agent) => this._patternCover(agent),
      [CombatPattern.SHOOT_AND_HIDE]: (agent) => this._patternShootAndHide(agent)
    };
  }

  /**
   * @typedef {object} AgentContext
   * @property {import('../ecs/Entity.js').Entity | null} entity Agent entity.
   * @property {import('../components/RangedAI.js').RangedAI | null} ai Agent state.
   * @property {import('../config/EnemyRanged.js').RangedTemplateConfig | null} config Resolved parameters.
   * @property {import('../components/Transform.js').Transform | null} transform Agent transform.
   * @property {import('../components/Velocity.js').Velocity | null} velocity Agent velocity.
   * @property {import('../ecs/Entity.js').Entity | null} player Live player, if any.
   * @property {import('../components/Transform.js').Transform | null} playerTransform Player transform.
   * @property {number} distance Planar distance to the current target.
   * @property {number} dt Delta time in seconds.
   */

  /** @returns {void} */
  init() {
    const { events } = this.ctx;
    this._subscriptions.push(
      events.on(Events.DAMAGE, (payload) => this._onDamage(payload)),
      events.on(Events.WEAPON_FIRED, (payload) => this._onWeaponFired(payload)),
      events.on(Events.BULLET_HIT, (payload) => this._onBulletHit(payload)),
      events.on(Events.DIED, (payload) => this._onDied(payload)),
      events.on(Events.ENTITY_DESTROYED, (entity) => this._disposeDebugMesh(entity))
    );
  }

  /** @returns {boolean} True while vision cones are drawn. */
  get debugVisible() {
    return this._debugVisible;
  }

  /**
   * Shows or hides the vision-cone overlay. Driven by the developer window
   * (`*`) rather than by a keyboard listener of this system's own (issue #56).
   *
   * @param {boolean} visible Desired state.
   * @returns {void}
   */
  setDebugVisible(visible) {
    this._debugVisible = Boolean(visible);
    if (!this._debugVisible) this._clearAllDebugMeshes();
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
    this._clearAllDebugMeshes();
    this._nav = null;
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    const agents = this.world.query(RangedAI, Transform, Velocity);
    if (agents.length === 0) {
      if (this._debugMeshes.size > 0) this._clearAllDebugMeshes();
      return;
    }

    this._ensureNavGrid();
    this._pathBudget = PATHS_PER_FRAME;

    const player = this._findPlayer();
    const playerTransform = player ? player.get(Transform) : null;

    for (const entity of agents) {
      const agent = this._agent;
      agent.entity = entity;
      agent.ai = entity.get(RangedAI);
      agent.config = agent.ai.config;
      agent.transform = entity.get(Transform);
      agent.velocity = entity.get(Velocity);
      agent.player = player;
      agent.playerTransform = playerTransform;
      agent.dt = dt;

      this._tick(agent);
    }

    this._applySeparation(agents);
    this._updateDebug(agents);
  }

  /**
   * One agent, one frame.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _tick(agent) {
    const { ai, entity } = agent;

    if (!ai.entered) {
      ai.entered = true;
      this._enterState(entity, ai);
    }

    ai.timeInState += agent.dt;
    ai.detectionTimer -= agent.dt;
    agent.distance = this._distanceToTarget(agent);

    this._resolveExpiredShots(entity, ai, agent.dt);
    this._trackStuck(agent);
    this._dropDeadTarget(agent);

    // Defaults for the frame: stand still, hold fire. A state has to ask.
    agent.velocity.desired.set(0, 0, 0);
    ai.fireBlocked = false;

    if (ai.stunRemaining > 0) {
      this._updateStun(agent);
      this._applyTrigger(agent);
      return;
    }

    const handler = this._states[ai.state] ?? this._states[RangedState.IDLE_STATIC];
    handler(agent);

    this._applyTrigger(agent);
  }

  /**
   * Thrown weapons knock a ranged-template enemy down without changing its FSM
   * state, so it resumes its previous behaviour once the timer expires.
   *
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _updateStun(agent) {
    const { ai, velocity } = agent;

    ai.stunRemaining = Math.max(0, ai.stunRemaining - agent.dt);
    ai.wantsToFire = false;
    ai.fireBlocked = true;
    ai.pendingShots.length = 0;
    this._clearRoute(ai);

    velocity.maxSpeed = 0;
    velocity.desired.set(0, 0, 0);
    velocity.linear.set(0, 0, 0);
    this._emitStunStars(agent);
  }

  // ------------------------------------------------------------------ states

  /**
   * Holds the spawn point, optionally sweeping its facing, and watches its cone.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _updateIdleStatic(agent) {
    const { ai, config, transform } = agent;
    agent.velocity.maxSpeed = 0;
    if (this._detectPlayer(agent)) return;

    if (config.idle_static_scan_angle > 0 && config.idle_rotation_speed > 0) {
      // A slow sinusoidal sweep around the spawn facing: the enemy looks alive
      // without ever leaving its post.
      const amplitude = toRadians(config.idle_static_scan_angle);
      const phase = ai.timeInState * config.idle_rotation_speed * 0.25;
      transform.rotationY = ai.spawnRotation + Math.sin(phase) * amplitude;
    }
  }

  /**
   * Wanders inside a circle centred on the spawn point.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _updateIdleRoaming(agent) {
    const { ai, config } = agent;
    agent.velocity.maxSpeed = config.idle_walk_speed;
    if (this._detectPlayer(agent)) return;

    // Only count the pause while standing still. Counting during travel would
    // make long routes skip the required idle wait at the destination.
    if (!ai.hasDestination) {
      ai.selectTimer -= agent.dt;
      if (ai.selectTimer <= 0) {
        this._pickWanderDestination(
          agent,
          ai.spawnX,
          ai.spawnZ,
          config.idle_roaming_path_range,
          config.idle_roaming_path_range
        );
        // Whether or not a path was found, wait a full interval before retrying
        // (or before leaving the destination after a successful walk).
        ai.selectTimer = config.idle_roaming_select_path_seconds;
      }
    }

    this._advanceRoute(agent, config.idle_walk_speed);
    this._faceMotion(agent);
  }

  /**
   * Patrols outward: each destination is picked around the *current* position,
   * so the agent progressively covers the map instead of orbiting its spawn.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _updateIdlePatrol(agent) {
    const { ai, config, transform } = agent;
    agent.velocity.maxSpeed = config.idle_walk_speed;
    if (this._detectPlayer(agent)) return;

    // Same pause rule as roaming: the select interval is idle time at a stop,
    // not a global cadence that runs while the agent is still walking.
    if (!ai.hasDestination) {
      ai.selectTimer -= agent.dt;
      if (ai.selectTimer <= 0) {
        this._pickWanderDestination(
          agent,
          transform.position.x,
          transform.position.z,
          config.idle_patrol_path_range,
          config.idle_patrol_max_distance_from_spawn
        );
        ai.selectTimer = config.idle_patrol_select_path_seconds;
      }
    }

    this._advanceRoute(agent, config.idle_walk_speed);
    this._faceMotion(agent);
  }

  /**
   * Ranged combat: keep the configured distance band, keep a firing line, and
   * remember the target for a while after losing sight of it.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _updateAggro(agent) {
    const { ai, config, entity } = agent;
    agent.velocity.maxSpeed = config.aggro_walk_speed;

    if (!this._isValidTarget(ai.target)) {
      // The target died or vanished: try to find another before giving up.
      if (this._detectPlayer(agent, true)) return;
      this._leaveCombat(agent, ai.target);
      return;
    }

    this._refreshVisibility(agent);

    if (ai.hasLineOfSight) {
      ai.memoryTimer = 0;
      const position = ai.target.get(Transform).position;
      ai.lastKnownX = position.x;
      ai.lastKnownZ = position.z;
      ai.hasLastKnown = true;
    } else {
      ai.memoryTimer += agent.dt;
      if (ai.memoryTimer >= config.enemy_aggro_target_memory_seconds) {
        this._leaveCombat(agent, ai.target);
        return;
      }
    }

    if (ai.forceReposition) {
      // Pathfinding is budgeted per frame. If the budget is already spent, keep
      // the flag so the forced reposition retries on a later frame instead of
      // being silently dropped (which would strand the failed-shot counter).
      if (this._pathBudget <= 0) {
        // Defer.
      } else if (this._repositionForAttack(agent)) {
        ai.forceReposition = false;
        this._maybeRerollPattern(agent);
      } else if (this._pathBudget > 0) {
        // Search finished with budget left: no workable attack position now.
        ai.forceReposition = false;
      }
      // else: budget ran out mid-search — retry next frame.
    }

    this._updatePattern(agent);
    (this._patterns[ai.pattern] ?? this._patterns[CombatPattern.STATIC_SHOOTER])(agent);
    this._updateFiring(agent);
    this._faceTarget(agent);

    // Keep the weapon's cadence honest even if a pattern changed mid-cooldown.
    const weapon = entity.get(Weapon);
    if (weapon) weapon.fireRate = 1 / config.enemy_weapon_shot_seconds;
  }

  // ------------------------------------------------------------ transitions

  /**
   * The one place a state may change.
   * @param {import('../ecs/Entity.js').Entity} entity Agent entity.
   * @param {import('../components/RangedAI.js').RangedAI} ai Agent state.
   * @param {string} next Target state.
   * @returns {void}
   * @private
   */
  _changeState(entity, ai, next) {
    if (ai.state === next) return;
    this._exitState(entity, ai);

    ai.previousState = ai.state;
    ai.state = next;
    ai.timeInState = 0;
    ai.entered = true;
    this._enterState(entity, ai);

    this.ctx.events.emit(Events.ENEMY_STATE_CHANGED, {
      entity,
      previousState: ai.previousState,
      newState: ai.state
    });
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Agent entity.
   * @param {import('../components/RangedAI.js').RangedAI} ai Agent state.
   * @returns {void}
   * @private
   */
  _enterState(entity, ai) {
    this._clearRoute(ai);
    ai.selectTimer = 0;

    if (ai.state !== RangedState.AGGRO_RANGED) return;

    ai.memoryTimer = 0;
    ai.failedShots = 0;
    ai.pendingShots.length = 0;
    ai.hasCover = false;
    ai.coverUnavailable = false;
    ai.coverTimer = 0;
    ai.coverPhase = 'exposed';
    ai.coverPhaseTimer = 0;
    ai.shotsThisCycle = 0;
    this._setPattern(entity, ai, pickCombatPattern(ai.config));
    this.ctx.events.emit(Events.ENEMY_ATTACK_STARTED, { entity, target: ai.target });
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Agent entity.
   * @param {import('../components/RangedAI.js').RangedAI} ai Agent state.
   * @returns {void}
   * @private
   */
  _exitState(entity, ai) {
    this._clearRoute(ai);
    if (ai.state === RangedState.AGGRO_RANGED) {
      const weapon = entity.get(Weapon);
      if (weapon) weapon.triggerHeld = false;
      ai.wantsToFire = false;
    }
  }

  /**
   * Assigns a target and drops into combat. Re-entering combat against the same
   * target is a no-op, so a burst of hits cannot restart the state.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Agent entity.
   * @param {import('../components/RangedAI.js').RangedAI} ai Agent state.
   * @param {import('../ecs/Entity.js').Entity} target New target.
   * @returns {void}
   * @private
   */
  _engage(entity, ai, target) {
    const changedTarget = ai.target !== target;
    ai.target = target;

    const position = target.get(Transform)?.position;
    if (position) {
      ai.lastKnownX = position.x;
      ai.lastKnownZ = position.z;
      ai.hasLastKnown = true;
    }
    ai.memoryTimer = 0;

    if (ai.state === RangedState.AGGRO_RANGED) {
      if (changedTarget) ai.failedShots = 0;
      return;
    }
    this._changeState(entity, ai, RangedState.AGGRO_RANGED);
  }

  /**
   * Gives up on the current target and returns to the configured idle state.
   * @param {AgentContext} agent Current agent.
   * @param {import('../ecs/Entity.js').Entity | null} lostTarget Target being dropped.
   * @returns {void}
   * @private
   */
  _leaveCombat(agent, lostTarget) {
    const { ai, entity } = agent;
    ai.target = null;
    ai.hasLineOfSight = false;
    this.ctx.events.emit(Events.ENEMY_TARGET_LOST, { entity, target: lostTarget ?? null });
    this._changeState(entity, ai, ai.config.enemy_aggro_lost_target_return_state);
  }

  // ------------------------------------------------------------------ senses

  /**
   * Vision cone: range, aperture and an unobstructed line of sight — all three,
   * because distance alone would let enemies see through walls.
   *
   * @param {AgentContext} agent Current agent.
   * @param {boolean} [force] Run the check even if the interval has not elapsed.
   * @returns {boolean} True when the player was detected this frame.
   * @private
   */
  _detectPlayer(agent, force = false) {
    const { ai, config, transform, player, playerTransform } = agent;
    if (!player || !playerTransform) return false;
    if (!force && ai.detectionTimer > 0) return false;
    ai.detectionTimer = config.enemy_detection_check_seconds;

    const dx = playerTransform.position.x - transform.position.x;
    const dz = playerTransform.position.z - transform.position.z;
    const distanceSq = dx * dx + dz * dz;

    ai.targetInCone = false;
    ai.hasLineOfSight = false;

    if (distanceSq > config.enemy_detection_cone_range ** 2) return false;

    const halfAperture = toRadians(config.enemy_detection_cone_angle) * 0.5;
    const bearing = Math.atan2(dx, dz);
    if (Math.abs(angleDelta(transform.rotationY, bearing)) > halfAperture) return false;
    ai.targetInCone = true;

    if (!this._lineOfSight(
      transform.position.x,
      transform.position.z,
      playerTransform.position.x,
      playerTransform.position.z
    )) {
      return false;
    }

    ai.hasLineOfSight = true;
    this.ctx.events.emit(Events.ENEMY_PLAYER_DETECTED, { entity: agent.entity, player });
    this._engage(agent.entity, ai, player);
    return true;
  }

  /**
   * Refreshes the line of sight to the current target. No cone test: an enemy
   * already in combat is turning to face its target anyway.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _refreshVisibility(agent) {
    const { ai, transform } = agent;
    if (ai.detectionTimer > 0) return;
    ai.detectionTimer = ai.config.enemy_detection_check_seconds;

    const target = ai.target.get(Transform);
    ai.targetInCone = true;
    ai.hasLineOfSight =
      agent.distance <= ai.config.enemy_detection_cone_range * 1.5 &&
      this._lineOfSight(
        transform.position.x,
        transform.position.z,
        target.position.x,
        target.position.z
      );
  }

  // -------------------------------------------------------------- navigation

  /**
   * Points at the arena's single shared navigation grid (built once per level
   * by {@link ../ai/sharedNav.js navGridForArena}; the melee FSM reads the same
   * instance). Null when there is no navigable arena yet.
   * @returns {void}
   * @private
   */
  _ensureNavGrid() {
    this._nav = navGridForArena(this.ctx.arena, this.ctx.config.dungeon?.wallHeight ?? 0);
  }

  /**
   * @param {number} ax Start X.
   * @param {number} az Start Z.
   * @param {number} bx End X.
   * @param {number} bz End Z.
   * @returns {boolean} True when nothing solid sits between the two points.
   * @private
   */
  _lineOfSight(ax, az, bx, bz) {
    return this._nav ? this._nav.hasLineOfSight(ax, az, bx, bz) : true;
  }

  /**
   * @param {number} x Candidate X.
   * @param {number} z Candidate Z.
   * @param {number} radius Body radius.
   * @returns {boolean} True when a body of that radius fits.
   * @private
   */
  _isClear(x, z, radius) {
    return this._nav ? this._nav.isClear(x, z, radius) : true;
  }

  /**
   * Picks a random navigable destination inside `range` of an origin and routes
   * to it, rejecting points inside geometry, points with no route, and routes
   * that would break the configured leash.
   *
   * @param {AgentContext} agent Current agent.
   * @param {number} originX Centre of the sampling disc.
   * @param {number} originZ Centre of the sampling disc.
   * @param {number} range Radius of the sampling disc.
   * @param {number} leash Maximum distance from the spawn point; `<= 0` disables it.
   * @returns {boolean} True when a destination was accepted.
   * @private
   */
  _pickWanderDestination(agent, originX, originZ, range, leash) {
    const { ai, config } = agent;
    if (range <= 0 || this._pathBudget <= 0) return false;

    for (let attempt = 0; attempt < config.idle_path_selection_max_attempts; attempt++) {
      const angle = Math.random() * TAU;
      // Bias away from the centre so the agent actually travels somewhere.
      const distance = range * (0.35 + 0.65 * Math.random());
      const x = originX + Math.sin(angle) * distance;
      const z = originZ + Math.cos(angle) * distance;

      if (leash > 0 && distanceSqXZ(x, z, ai.spawnX, ai.spawnZ) > leash * leash) continue;
      if (!this._isClear(x, z, ai.radius)) continue;
      if (this._setRoute(agent, x, z, leash)) return true;
      if (this._pathBudget <= 0) return false;
    }
    return false;
  }

  /**
   * Computes a route and installs it.
   * @param {AgentContext} agent Current agent.
   * @param {number} x Destination X.
   * @param {number} z Destination Z.
   * @param {number} [leash] Maximum waypoint distance from the spawn point.
   * @returns {boolean} True when a valid route was found.
   * @private
   */
  _setRoute(agent, x, z, leash = 0) {
    const { ai, transform } = agent;

    /** @type {Array<{ x: number, z: number }> | null} */
    let path = null;
    if (this._nav) {
      if (this._pathBudget <= 0) return false;
      this._pathBudget--;
      path = this._nav.findPath(
        transform.position.x,
        transform.position.z,
        x,
        z,
        ai.radius
      );
    } else {
      path = [{ x, z }];
    }
    if (!path || path.length === 0) return false;

    if (leash > 0) {
      for (const point of path) {
        if (distanceSqXZ(point.x, point.z, ai.spawnX, ai.spawnZ) > leash * leash) return false;
      }
    }

    ai.path = path;
    ai.pathIndex = 0;
    ai.hasDestination = true;
    ai.destinationX = x;
    ai.destinationZ = z;
    ai.repathTimer = ai.config.navigation_repath_seconds;
    ai.stuckTimer = 0;
    ai.stuckDistance = 0;
    return true;
  }

  /**
   * @param {import('../components/RangedAI.js').RangedAI} ai Agent state.
   * @returns {void}
   * @private
   */
  _clearRoute(ai) {
    ai.path = [];
    ai.pathIndex = 0;
    ai.hasDestination = false;
    ai.stuckTimer = 0;
    ai.stuckDistance = 0;
  }

  /**
   * Walks the active route. Returns false once there is nothing left to walk,
   * which is the signal a pattern uses to decide it has arrived.
   *
   * @param {AgentContext} agent Current agent.
   * @param {number} speed Speed to travel at.
   * @returns {boolean} True while the agent is still travelling.
   * @private
   */
  _advanceRoute(agent, speed) {
    const { ai, config, transform, velocity } = agent;
    if (!ai.hasDestination || ai.path.length === 0) return false;

    ai.repathTimer -= agent.dt;

    let waypoint = ai.path[ai.pathIndex];
    let dx = waypoint.x - transform.position.x;
    let dz = waypoint.z - transform.position.z;
    let distance = Math.hypot(dx, dz);

    while (distance <= config.navigation_arrival_tolerance) {
      ai.pathIndex++;
      if (ai.pathIndex >= ai.path.length) {
        this._clearRoute(ai);
        return false;
      }
      waypoint = ai.path[ai.pathIndex];
      dx = waypoint.x - transform.position.x;
      dz = waypoint.z - transform.position.z;
      distance = Math.hypot(dx, dz);
    }

    velocity.desired.set((dx / distance) * speed, 0, (dz / distance) * speed);
    return true;
  }

  /**
   * Cancels a route the agent is no longer making progress along. Without this
   * an enemy wedged on a corner would push into it forever.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _trackStuck(agent) {
    const { ai, config, transform } = agent;
    const moved = Math.hypot(
      transform.position.x - ai.lastX,
      transform.position.z - ai.lastZ
    );
    ai.lastX = transform.position.x;
    ai.lastZ = transform.position.z;

    if (!ai.hasDestination) {
      ai.stuckTimer = 0;
      ai.stuckDistance = 0;
      return;
    }

    ai.stuckTimer += agent.dt;
    ai.stuckDistance += moved;
    if (ai.stuckTimer < config.navigation_stuck_detection_seconds) return;

    const stuck = ai.stuckDistance < config.navigation_stuck_distance_threshold;
    ai.stuckTimer = 0;
    ai.stuckDistance = 0;
    if (stuck) {
      this._clearRoute(ai);
      // Spec: cancel the blocked route and pick a new destination promptly.
      // Without this, idle agents would wait a full select interval after a jam.
      ai.selectTimer = 0;
    }
  }

  // ---------------------------------------------------------------- patterns

  /**
   * Re-rolls the combat pattern when its slot expires or when it cannot be
   * executed any more. The minimum duration is what stops an enemy flickering
   * between patterns every frame.
   *
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _updatePattern(agent) {
    const { ai, config, entity } = agent;
    ai.patternTimer -= agent.dt;

    const usesCover =
      ai.pattern === CombatPattern.COVER_SHOOTER ||
      ai.pattern === CombatPattern.SHOOT_AND_HIDE;

    if (usesCover && ai.coverUnavailable) {
      this._setPattern(entity, ai, config.aggro_cover_fallback_pattern);
      return;
    }
    if (ai.patternTimer > 0) return;

    this._setPattern(entity, ai, pickCombatPattern(config));
  }

  /**
   * Re-rolls only if the current pattern has had its minimum airtime.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _maybeRerollPattern(agent) {
    const { ai, config, entity } = agent;
    const elapsed = ai.patternDuration - ai.patternTimer;
    if (elapsed < config.aggro_pattern_minimum_duration_seconds) return;
    this._setPattern(entity, ai, pickCombatPattern(config));
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Agent entity.
   * @param {import('../components/RangedAI.js').RangedAI} ai Agent state.
   * @param {string} pattern New pattern.
   * @returns {void}
   * @private
   */
  _setPattern(entity, ai, pattern) {
    const { config } = ai;
    const previous = ai.pattern;

    ai.patternDuration =
      config.aggro_pattern_minimum_duration_seconds +
      Math.random() *
        Math.max(
          0,
          config.aggro_pattern_maximum_duration_seconds -
            config.aggro_pattern_minimum_duration_seconds
        );
    ai.patternTimer = ai.patternDuration;

    if (previous === pattern) return;

    ai.pattern = pattern;
    ai.shotsThisCycle = 0;
    ai.coverPhase = 'exposed';
    ai.coverPhaseTimer = 0;
    this.ctx.events.emit(Events.ENEMY_PATTERN_CHANGED, {
      entity,
      previousPattern: previous,
      newPattern: pattern
    });
  }

  /**
   * Shared distance keeping: close in when too far, back off when too close.
   * Both cases route through the navigation grid rather than walking a straight
   * line into whatever happens to be in the way.
   *
   * @param {AgentContext} agent Current agent.
   * @returns {boolean} True when it took control of the agent's movement.
   * @private
   */
  _maintainDistance(agent) {
    const { ai, config } = agent;
    const tooFar = agent.distance > config.enemy_aggro_distance_max;
    const tooClose = agent.distance < config.enemy_aggro_distance_min;

    if (!tooFar && !tooClose) return false;

    if (ai.hasDestination && ai.repathTimer > 0) {
      return this._advanceRoute(agent, config.aggro_walk_speed);
    }
    if (this._seekBandPosition(agent)) {
      return this._advanceRoute(agent, config.aggro_walk_speed);
    }

    // No navigable spot at the ideal distance: at least stop closing in.
    if (tooClose) this._steerAway(agent, config.aggro_walk_speed);
    return true;
  }

  /**
   * Routes to a point at the ideal distance from the target, preferring the
   * agent's current bearing so it does not swing around the player.
   * @param {AgentContext} agent Current agent.
   * @returns {boolean} True when a route was installed.
   * @private
   */
  _seekBandPosition(agent) {
    const { ai, config, transform } = agent;
    const target = ai.target.get(Transform).position;
    const ideal = (config.enemy_aggro_distance_min + config.enemy_aggro_distance_max) * 0.5;

    const dx = transform.position.x - target.x;
    const dz = transform.position.z - target.z;
    const bearing = Math.atan2(dx, dz);

    for (let attempt = 0; attempt < REPOSITION_ATTEMPTS; attempt++) {
      // Attempt 0 keeps the bearing; later ones fan out to both sides.
      const spread = attempt === 0 ? 0 : (attempt % 2 === 0 ? 1 : -1) * (0.35 * Math.ceil(attempt / 2));
      const angle = bearing + spread;
      const x = target.x + Math.sin(angle) * ideal;
      const z = target.z + Math.cos(angle) * ideal;

      if (!this._isClear(x, z, ai.radius)) continue;
      if (!this._lineOfSight(x, z, target.x, target.z)) continue;
      if (this._setRoute(agent, x, z)) return true;
      if (this._pathBudget <= 0) return false;
    }
    return false;
  }

  /**
   * Looks for a navigable spot inside the distance band that can see the
   * target, and walks to it.
   * @param {AgentContext} agent Current agent.
   * @returns {boolean} True when a new attack position was accepted.
   * @private
   */
  _repositionForAttack(agent) {
    const { ai, config, entity } = agent;
    if (!this._isValidTarget(ai.target)) return false;

    const target = ai.target.get(Transform).position;
    const min = config.enemy_aggro_distance_min;
    const max = config.enemy_aggro_distance_max;

    for (let attempt = 0; attempt < REPOSITION_ATTEMPTS; attempt++) {
      const angle = Math.random() * TAU;
      const distance = min + Math.random() * Math.max(0.001, max - min);
      const x = target.x + Math.sin(angle) * distance;
      const z = target.z + Math.cos(angle) * distance;

      if (!this._isClear(x, z, ai.radius)) continue;
      if (!this._lineOfSight(x, z, target.x, target.z)) continue;
      if (!this._setRoute(agent, x, z)) {
        if (this._pathBudget <= 0) return false;
        continue;
      }

      this.ctx.events.emit(Events.ENEMY_REPOSITION_STARTED, {
        entity,
        destination: { x, z }
      });
      return true;
    }
    return false;
  }

  /**
   * Stands its ground and shoots, moving only when it has to.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _patternStatic(agent) {
    const { ai, config } = agent;
    if (this._maintainDistance(agent)) return;
    if (this._advanceRoute(agent, config.aggro_walk_speed)) return;
    if (!ai.hasLineOfSight) {
      this._repositionForAttack(agent);
      this._advanceRoute(agent, config.aggro_walk_speed);
    }
    // Otherwise: braked, which is the default for the frame.
  }

  /**
   * Keeps moving while shooting, slowing down while the trigger is down.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _patternMoving(agent) {
    const { ai, config, transform } = agent;
    const speed = ai.wantsToFire
      ? config.aggro_walk_speed * config.aggro_moving_shot_speed_multiplier
      : config.aggro_walk_speed;

    if (this._maintainDistance(agent)) return;
    if (this._advanceRoute(agent, speed)) return;

    // Inside the band: drift along the ideal ring so it never becomes a turret.
    const target = ai.target.get(Transform).position;
    const ideal = (config.enemy_aggro_distance_min + config.enemy_aggro_distance_max) * 0.5;
    const bearing = Math.atan2(
      transform.position.x - target.x,
      transform.position.z - target.z
    );
    const angle = bearing + ai.strafeSign * 0.35;
    this._steerTowards(
      agent,
      target.x + Math.sin(angle) * ideal,
      target.z + Math.cos(angle) * ideal,
      speed
    );
  }

  /**
   * Circles the target, flipping direction on a timer and whenever the way is
   * blocked.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _patternStrafe(agent) {
    const { ai, config, transform } = agent;
    if (this._maintainDistance(agent)) return;
    if (this._advanceRoute(agent, config.aggro_walk_speed)) return;

    ai.strafeTimer -= agent.dt;
    if (ai.strafeTimer <= 0) {
      ai.strafeSign *= -1;
      ai.strafeTimer = config.aggro_strafe_change_direction_seconds;
    }

    const target = ai.target.get(Transform).position;
    const dx = transform.position.x - target.x;
    const dz = transform.position.z - target.z;
    const length = Math.hypot(dx, dz) || 1;

    for (let attempt = 0; attempt < 2; attempt++) {
      // Perpendicular to the line of sight, so the distance band is preserved.
      const x = transform.position.x + (-dz / length) * config.aggro_strafe_distance * ai.strafeSign;
      const z = transform.position.z + (dx / length) * config.aggro_strafe_distance * ai.strafeSign;

      if (this._isWalkableStep(agent, x, z)) {
        this._steerTowards(agent, x, z, config.aggro_walk_speed);
        return;
      }
      // Blocked that way: turn around rather than grind along the wall.
      ai.strafeSign *= -1;
      ai.strafeTimer = config.aggro_strafe_change_direction_seconds;
    }
  }

  /**
   * Fights from cover: hide, step out to fire, step back.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _patternCover(agent) {
    this._runCoverCycle(agent, 1, Infinity, 0, true);
  }

  /**
   * Alternates a burst from an exposed position with a spell behind cover.
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _patternShootAndHide(agent) {
    const { config } = agent;
    this._runCoverCycle(
      agent,
      config.aggro_shoot_and_hide_shots_per_cycle,
      config.aggro_shoot_and_hide_exposed_seconds,
      config.aggro_shoot_and_hide_hidden_seconds,
      false
    );
  }

  /**
   * Shared machinery of the two cover patterns.
   *
   * @param {AgentContext} agent Current agent.
   * @param {number} shotsPerCycle Shots fired before ducking back.
   * @param {number} exposedSeconds Cap on time spent exposed.
   * @param {number} hiddenSeconds Time spent hidden before peeking again.
   * @param {boolean} waitForWeapon Whether to stay hidden until the weapon is ready.
   * @returns {void}
   * @private
   */
  _runCoverCycle(agent, shotsPerCycle, exposedSeconds, hiddenSeconds, waitForWeapon) {
    const { ai, config, entity } = agent;

    ai.coverTimer -= agent.dt;
    if (!ai.hasCover || ai.coverTimer <= 0) {
      ai.coverTimer = config.aggro_cover_reselection_seconds;
      this._findCover(agent);
    }
    if (!ai.hasCover) {
      // No usable cover: the pattern is unviable and `_updatePattern` will swap
      // it out on the next tick. Keep fighting in the meantime.
      ai.coverUnavailable = true;
      this._patternStatic(agent);
      return;
    }

    if (ai.coverPhase === 'hiding') {
      ai.fireBlocked = true;
      ai.coverPhaseTimer -= agent.dt;

      const weapon = entity.get(Weapon);
      const weaponReady = !waitForWeapon || !weapon || weapon.cooldown <= 0;
      const arrived = !this._moveTo(agent, ai.coverX, ai.coverZ, config.aggro_walk_speed);

      if (arrived && ai.coverPhaseTimer <= 0 && weaponReady) {
        ai.coverPhase = 'exposed';
        ai.coverPhaseTimer = exposedSeconds;
        ai.shotsThisCycle = 0;
      }
      return;
    }

    // Exposed: get to the firing position and shoot from it.
    ai.coverPhaseTimer -= agent.dt;
    this._moveTo(agent, ai.peekX, ai.peekZ, config.aggro_walk_speed);

    if (ai.shotsThisCycle >= shotsPerCycle || ai.coverPhaseTimer <= 0) {
      ai.coverPhase = 'hiding';
      ai.coverPhaseTimer = hiddenSeconds;
      ai.shotsThisCycle = 0;
    }
  }

  /**
   * Looks for a spot that breaks the target's line of fire but has a firing
   * position next to it. Blockers shorter than `aggro_cover_minimum_height`
   * are not cover at all.
   *
   * @param {AgentContext} agent Current agent.
   * @returns {boolean} True when cover was found.
   * @private
   */
  _findCover(agent) {
    const { ai, config, transform } = agent;
    ai.hasCover = false;
    if (!this._isValidTarget(ai.target)) return false;

    if (this._nav && this._nav.solidHeight < config.aggro_cover_minimum_height) {
      ai.coverUnavailable = true;
      return false;
    }

    const target = ai.target.get(Transform).position;
    const searchRange = config.aggro_cover_search_range;

    for (let attempt = 0; attempt < COVER_ATTEMPTS; attempt++) {
      const angle = Math.random() * TAU;
      const distance = 2 + Math.random() * Math.max(0.001, searchRange - 2);
      const x = transform.position.x + Math.sin(angle) * distance;
      const z = transform.position.z + Math.cos(angle) * distance;

      if (!this._isClear(x, z, ai.radius)) continue;
      // Cover is defined by what it blocks.
      if (this._lineOfSight(x, z, target.x, target.z)) continue;

      const peek = this._findPeekPosition(agent, x, z, target);
      if (!peek) continue;

      ai.hasCover = true;
      ai.coverUnavailable = false;
      ai.coverX = x;
      ai.coverZ = z;
      ai.peekX = peek.x;
      ai.peekZ = peek.z;
      ai.coverPhase = 'hiding';
      ai.coverPhaseTimer = 0;
      return true;
    }

    ai.coverUnavailable = true;
    return false;
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @param {number} coverX Cover X.
   * @param {number} coverZ Cover Z.
   * @param {{ x: number, z: number }} target Target position.
   * @returns {{ x: number, z: number } | null} A position next to cover with a firing line.
   * @private
   */
  _findPeekPosition(agent, coverX, coverZ, target) {
    const { ai, config } = agent;
    for (let step = 0; step < PEEK_OFFSETS; step++) {
      const angle = (step / PEEK_OFFSETS) * TAU;
      const x = coverX + Math.sin(angle) * PEEK_DISTANCE;
      const z = coverZ + Math.cos(angle) * PEEK_DISTANCE;

      if (!this._isClear(x, z, ai.radius)) continue;
      if (!this._lineOfSight(x, z, target.x, target.z)) continue;

      const distance = Math.hypot(x - target.x, z - target.z);
      if (distance > config.enemy_aggro_distance_max * 1.15) continue;
      if (distance < config.enemy_aggro_distance_min * 0.75) continue;

      return { x, z };
    }
    return null;
  }

  /**
   * Routes to a fixed point, reusing the active route while it is still valid.
   * @param {AgentContext} agent Current agent.
   * @param {number} x Destination X.
   * @param {number} z Destination Z.
   * @param {number} speed Travel speed.
   * @returns {boolean} True while still travelling.
   * @private
   */
  _moveTo(agent, x, z, speed) {
    const { ai, config, transform } = agent;
    const arrived =
      distanceSqXZ(transform.position.x, transform.position.z, x, z) <=
      config.navigation_arrival_tolerance ** 2;
    if (arrived) {
      if (ai.hasDestination) this._clearRoute(ai);
      return false;
    }

    const sameDestination =
      ai.hasDestination &&
      distanceSqXZ(ai.destinationX, ai.destinationZ, x, z) < 0.25;

    if (!sameDestination || (!ai.hasDestination && ai.repathTimer <= 0)) {
      if (!this._setRoute(agent, x, z)) {
        this._steerTowards(agent, x, z, speed);
        return true;
      }
    }
    if (!this._advanceRoute(agent, speed)) return false;
    return true;
  }

  // ----------------------------------------------------------------- firing

  /**
   * Decides whether the trigger may be pulled this frame. Every condition the
   * spec lists is checked here, in one place, so no pattern can shoot through
   * a wall or from outside its effective range.
   *
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _updateFiring(agent) {
    const { ai, config, entity, transform } = agent;
    ai.wantsToFire = false;

    if (ai.fireBlocked) return;
    const weapon = entity.get(Weapon);
    if (!weapon) return;
    if (!this._isValidTarget(ai.target)) return;
    if (!ai.hasLineOfSight) return;

    const effectiveRange = Math.min(weapon.range, config.enemy_aggro_distance_max * 1.15);
    if (agent.distance > effectiveRange) return;

    const target = ai.target.get(Transform).position;
    const bearing = Math.atan2(
      target.x - transform.position.x,
      target.z - transform.position.z
    );
    if (Math.abs(angleDelta(transform.rotationY, bearing)) > AIM_TOLERANCE) return;

    // The muzzle sits ahead of the body: check the shot from where it actually
    // leaves, not from the agent's centre.
    const muzzleX = transform.position.x + Math.sin(transform.rotationY) * weapon.muzzleForward;
    const muzzleZ = transform.position.z + Math.cos(transform.rotationY) * weapon.muzzleForward;
    if (!this._lineOfSight(muzzleX, muzzleZ, target.x, target.z)) return;

    ai.wantsToFire = true;
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _applyTrigger(agent) {
    const weapon = agent.entity.get(Weapon);
    if (weapon) weapon.triggerHeld = agent.ai.wantsToFire;
  }

  /**
   * Ring of stars orbiting a stunned ranged-template enemy.
   *
   * @param {AgentContext} agent Stunned agent.
   * @returns {void}
   * @private
   */
  _emitStunStars(agent) {
    const { ai } = agent;
    const stun = this.ctx.config.throwing;

    ai.stunFxCooldown -= agent.dt;
    if (ai.stunFxCooldown > 0) return;
    ai.stunFxCooldown = stun.stunStarInterval;
    ai.stunSpin += stun.stunStarSpin;

    const step = (Math.PI * 2) / stun.stunStarCount;
    for (let index = 0; index < stun.stunStarCount; index++) {
      const angle = ai.stunSpin + index * step;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      this._starPosition.set(
        agent.transform.position.x + cos * stun.stunStarRadius,
        agent.transform.position.y + stun.stunStarRadius * 1.6,
        agent.transform.position.z + sin * stun.stunStarRadius
      );
      this._starDirection.set(-sin, 0.85, cos);
      this.ctx.events.emit(Events.FX_BURST, this._starEvent);
    }
  }

  /**
   * Ages the rounds in flight. One that never resolves flew past the target,
   * which the spec counts as a miss.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Agent entity.
   * @param {import('../components/RangedAI.js').RangedAI} ai Agent state.
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   * @private
   */
  _resolveExpiredShots(entity, ai, dt) {
    if (ai.pendingShots.length === 0) return;
    for (let index = 0; index < ai.pendingShots.length; index++) {
      ai.pendingShots[index] -= dt;
    }
    while (ai.pendingShots.length > 0 && ai.pendingShots[0] <= 0) {
      ai.pendingShots.shift();
      this._registerShotResult(entity, ai, false);
    }
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Agent entity.
   * @param {import('../components/RangedAI.js').RangedAI} ai Agent state.
   * @param {boolean} hit Whether the round connected with the target.
   * @returns {void}
   * @private
   */
  _registerShotResult(entity, ai, hit) {
    if (hit) {
      ai.failedShots = 0;
      this.ctx.events.emit(Events.ENEMY_SHOT_HIT, { entity, target: ai.target });
      return;
    }

    ai.failedShots++;
    this.ctx.events.emit(Events.ENEMY_SHOT_MISSED, { entity, target: ai.target });

    if (ai.failedShots < ai.config.enemy_aggro_failed_shots_for_reposition) return;
    ai.failedShots = 0;
    ai.forceReposition = true;
  }

  // ------------------------------------------------------------------ events

  /**
   * @param {{ target: import('../ecs/Entity.js').Entity, amount: number,
   *           source: import('../ecs/Entity.js').Entity | null }} payload Damage event.
   * @returns {void}
   * @private
   */
  _onDamage(payload) {
    const { target, source, amount } = payload;

    if (source && source.alive && source.has(RangedAI)) {
      const ai = source.get(RangedAI);
      // Only rounds aimed at the current target count toward the tally, and only
      // when the target can actually take damage. Hits during i-frames are not
      // "valid impacts" — HealthSystem will ignore them — so treating them as
      // hits would falsely reset the failed-shot counter.
      const health = target?.get?.(Health);
      const damageApplies =
        health && !health.dead && health.invulnerableFor <= 0 && amount > 0;
      if (target === ai.target && ai.pendingShots.length > 0 && damageApplies) {
        ai.pendingShots.shift();
        this._registerShotResult(source, ai, true);
      }
    }

    if (!target || !target.alive || !target.has(RangedAI)) return;
    const ai = target.get(RangedAI);

    this.ctx.events.emit(Events.ENEMY_DAMAGE_RECEIVED, {
      entity: target,
      source: source ?? null,
      amount
    });

    // Being shot pulls an enemy into combat even from behind.
    const attacker = source && source.alive && source.has(PlayerControlled) ? source : null;
    if (attacker) {
      this._engage(target, ai, attacker);
      return;
    }
    if (ai.state !== RangedState.AGGRO_RANGED && this._isValidTarget(ai.target)) {
      this._changeState(target, ai, RangedState.AGGRO_RANGED);
    }
  }

  /**
   * @param {{ owner: import('../ecs/Entity.js').Entity }} payload Weapon event.
   * @returns {void}
   * @private
   */
  _onWeaponFired({ owner }) {
    if (!owner || !owner.alive || !owner.has(RangedAI)) return;
    const ai = owner.get(RangedAI);
    const weapon = owner.get(Weapon);

    // Time the round needs to cover its whole range, plus a small grace period.
    const lifetime = weapon ? weapon.range / Math.max(1, weapon.projectileSpeed) + 0.35 : 1;
    ai.pendingShots.push(lifetime);
    ai.shotsThisCycle++;

    this.ctx.events.emit(Events.ENEMY_WEAPON_FIRED, {
      entity: owner,
      weapon,
      target: ai.target
    });
  }

  /**
   * @param {{ owner: import('../ecs/Entity.js').Entity | null,
   *           target: import('../ecs/Entity.js').Entity | null }} payload Impact event.
   * @returns {void}
   * @private
   */
  _onBulletHit({ owner, target }) {
    if (!owner || !owner.alive || !owner.has(RangedAI)) return;
    // Impacts against something damageable were already resolved by `damage`.
    if (target && target.has(Health)) return;

    const ai = owner.get(RangedAI);
    if (ai.pendingShots.length === 0) return;
    ai.pendingShots.shift();
    this._registerShotResult(owner, ai, false);
  }

  /**
   * @param {{ entity: import('../ecs/Entity.js').Entity }} payload Death event.
   * @returns {void}
   * @private
   */
  _onDied({ entity }) {
    if (!entity || !entity.has(RangedAI)) return;
    const weapon = entity.get(Weapon);
    if (weapon) weapon.triggerHeld = false;
    entity.get(RangedAI).wantsToFire = false;
    this.ctx.events.emit(Events.ENEMY_DEATH, { entity });
  }

  // ------------------------------------------------------------------ helpers

  /**
   * @returns {import('../ecs/Entity.js').Entity | null} The live player, if any.
   * @private
   */
  _findPlayer() {
    for (const player of this.world.query(PlayerControlled, Transform, Health)) {
      if (!player.get(Health).dead) return player;
    }
    return null;
  }

  /**
   * @param {import('../ecs/Entity.js').Entity | null} target Candidate target.
   * @returns {boolean}
   * @private
   */
  _isValidTarget(target) {
    if (!target || !target.alive || !target.has(Transform)) return false;
    const health = target.get(Health);
    return !health || !health.dead;
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _dropDeadTarget(agent) {
    const { ai } = agent;
    if (ai.target && !this._isValidTarget(ai.target)) ai.target = null;
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @returns {number} Planar distance to the target, or Infinity.
   * @private
   */
  _distanceToTarget(agent) {
    const target = agent.ai.target;
    if (!this._isValidTarget(target)) return Infinity;
    const position = target.get(Transform).position;
    return Math.sqrt(
      distanceSqXZ(
        agent.transform.position.x,
        agent.transform.position.z,
        position.x,
        position.z
      )
    );
  }

  /**
   * Steers straight at a point, but only after the navigation grid confirms the
   * step is walkable — short moves inside combat do not deserve a full A* run.
   *
   * @param {AgentContext} agent Current agent.
   * @param {number} x Target X.
   * @param {number} z Target Z.
   * @param {number} speed Travel speed.
   * @returns {void}
   * @private
   */
  _steerTowards(agent, x, z, speed) {
    const { transform, velocity } = agent;
    const dx = x - transform.position.x;
    const dz = z - transform.position.z;
    const distance = Math.hypot(dx, dz);
    if (distance < 1e-4) return;
    velocity.desired.set((dx / distance) * speed, 0, (dz / distance) * speed);
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @param {number} speed Travel speed.
   * @returns {void}
   * @private
   */
  _steerAway(agent, speed) {
    const { ai, transform } = agent;
    if (!this._isValidTarget(ai.target)) return;
    const target = ai.target.get(Transform).position;
    const dx = transform.position.x - target.x;
    const dz = transform.position.z - target.z;
    const length = Math.hypot(dx, dz) || 1;
    this._steerTowards(
      agent,
      transform.position.x + (dx / length) * 4,
      transform.position.z + (dz / length) * 4,
      speed
    );
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @param {number} x Step target X.
   * @param {number} z Step target Z.
   * @returns {boolean} True when the agent can walk straight there.
   * @private
   */
  _isWalkableStep(agent, x, z) {
    if (!this._nav) return true;
    const { transform, ai } = agent;
    return this._nav.isSegmentClear(
      transform.position.x,
      transform.position.z,
      x,
      z,
      ai.radius
    );
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _faceTarget(agent) {
    const { ai, config, transform } = agent;
    let x;
    let z;

    if (this._isValidTarget(ai.target) && ai.hasLineOfSight) {
      const position = ai.target.get(Transform).position;
      x = position.x;
      z = position.z;
    } else if (ai.hasLastKnown) {
      // Keep aiming at where the target was last seen.
      x = ai.lastKnownX;
      z = ai.lastKnownZ;
    } else {
      return;
    }

    const dx = x - transform.position.x;
    const dz = z - transform.position.z;
    if (dx * dx + dz * dz < 1e-6) return;

    transform.rotationY = dampAngle(
      transform.rotationY,
      Math.atan2(dx, dz),
      config.aggro_rotation_speed,
      agent.dt
    );
  }

  /**
   * @param {AgentContext} agent Current agent.
   * @returns {void}
   * @private
   */
  _faceMotion(agent) {
    const { config, transform, velocity } = agent;
    const dx = velocity.desired.x;
    const dz = velocity.desired.z;
    if (dx * dx + dz * dz < 1e-4) return;

    transform.rotationY = dampAngle(
      transform.rotationY,
      Math.atan2(dx, dz),
      config.idle_rotation_speed,
      agent.dt
    );
  }

  /**
   * Keeps agents from stacking on top of each other.
   * @param {import('../ecs/Entity.js').Entity[]} agents Every ranged agent this frame.
   * @returns {void}
   * @private
   */
  _applySeparation(agents) {
    if (agents.length < 2) return;

    for (const entity of agents) {
      const ai = entity.get(RangedAI);
      if (ai.stunRemaining > 0 || ai.fireBlocked) continue;
      const transform = entity.get(Transform);
      const velocity = entity.get(Velocity);
      const radius = ai.separationRadius;
      const radiusSq = radius * radius;

      let pushX = 0;
      let pushZ = 0;

      for (const other of agents) {
        if (other === entity) continue;
        const otherPosition = other.get(Transform).position;
        const dx = transform.position.x - otherPosition.x;
        const dz = transform.position.z - otherPosition.z;
        const distanceSq = dx * dx + dz * dz;
        if (distanceSq > radiusSq || distanceSq < 1e-6) continue;

        const strength = 1 - Math.sqrt(distanceSq) / radius;
        const inverse = strength / Math.sqrt(distanceSq);
        pushX += dx * inverse;
        pushZ += dz * inverse;
      }

      if (pushX === 0 && pushZ === 0) continue;
      velocity.desired.x += pushX * ai.separationStrength;
      velocity.desired.z += pushZ * ai.separationStrength;
    }
  }

  // ------------------------------------------------------------------- debug

  /**
   * Draws vision cones and AI state markers when debug is toggled with `*`.
   * @param {import('../ecs/Entity.js').Entity[]} agents Live ranged agents.
   * @returns {void}
   * @private
   */
  _updateDebug(agents) {
    if (!this._debugVisible || !isDevBuild()) {
      if (this._debugMeshes.size > 0) this._clearAllDebugMeshes();
      return;
    }

    const live = new Set();
    for (const entity of agents) {
      live.add(entity.id);
      this._drawDebugFor(entity);
    }

    for (const id of this._debugMeshes.keys()) {
      if (!live.has(id)) this._disposeDebugMeshById(id);
    }
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Agent entity.
   * @returns {void}
   * @private
   */
  _drawDebugFor(entity) {
    const ai = entity.get(RangedAI);
    const transform = entity.get(Transform);
    if (!ai || !transform) return;

    const mesh = this._ensureDebugMesh(entity);
    const positions = mesh.geometry.attributes.position.array;
    const { config } = ai;
    const range = config.enemy_detection_cone_range;
    const half = toRadians(config.enemy_detection_cone_angle) * 0.5;
    const facing = transform.rotationY;
    const ox = transform.position.x;
    const oy = 0.15;
    const oz = transform.position.z;

    let cursor = 0;
    const write = (x, y, z) => {
      positions[cursor++] = x;
      positions[cursor++] = y;
      positions[cursor++] = z;
    };

    // Cone outline: origin → left edge → arc → right edge → origin.
    write(ox, oy, oz);
    write(ox + Math.sin(facing - half) * range, oy, oz + Math.cos(facing - half) * range);
    for (let i = 0; i <= DEBUG_CONE_SEGMENTS; i++) {
      const t = i / DEBUG_CONE_SEGMENTS;
      const angle = facing - half + (half * 2) * t;
      write(ox + Math.sin(angle) * range, oy, oz + Math.cos(angle) * range);
    }
    write(ox + Math.sin(facing + half) * range, oy, oz + Math.cos(facing + half) * range);
    write(ox, oy, oz);

    // Forward ray showing current facing.
    write(ox, oy, oz);
    write(ox + Math.sin(facing) * range, oy, oz + Math.cos(facing) * range);

    // Spawn marker + destination marker when present.
    write(ai.spawnX, oy, ai.spawnZ);
    write(ai.spawnX, oy + 0.6, ai.spawnZ);
    if (ai.hasDestination) {
      write(ox, oy, oz);
      write(ai.destinationX, oy, ai.destinationZ);
    } else {
      write(ox, oy, oz);
      write(ox, oy, oz);
    }

    // Combat band ring samples (min + max) as two short diametric lines.
    if (ai.state === RangedState.AGGRO_RANGED && this._isValidTarget(ai.target)) {
      const target = ai.target.get(Transform).position;
      write(target.x - config.enemy_aggro_distance_min, oy, target.z);
      write(target.x + config.enemy_aggro_distance_min, oy, target.z);
      write(target.x, oy, target.z - config.enemy_aggro_distance_max);
      write(target.x, oy, target.z + config.enemy_aggro_distance_max);
    } else {
      write(ox, oy, oz);
      write(ox, oy, oz);
      write(ox, oy, oz);
      write(ox, oy, oz);
    }

    // Last-known target position.
    if (ai.hasLastKnown) {
      write(ai.lastKnownX - 0.4, oy, ai.lastKnownZ);
      write(ai.lastKnownX + 0.4, oy, ai.lastKnownZ);
      write(ai.lastKnownX, oy, ai.lastKnownZ - 0.4);
      write(ai.lastKnownX, oy, ai.lastKnownZ + 0.4);
    } else {
      write(ox, oy, oz);
      write(ox, oy, oz);
      write(ox, oy, oz);
      write(ox, oy, oz);
    }

    mesh.geometry.attributes.position.needsUpdate = true;
    mesh.geometry.setDrawRange(0, cursor / 3);

    // Colour encodes detection state for the cone.
    let color = DEBUG_COLOR_NEUTRAL;
    if (ai.state === RangedState.AGGRO_RANGED || ai.hasLineOfSight) {
      color = DEBUG_COLOR_DETECTED;
    } else if (ai.targetInCone) {
      color = DEBUG_COLOR_WARNING;
    }
    /** @type {THREE.LineBasicMaterial} */ (mesh.material).color.setHex(color);

    // Store a human-readable snapshot on the mesh for optional HUD/console use.
    mesh.userData.aiDebug = {
      state: ai.state,
      pattern: ai.pattern,
      target: ai.target?.id ?? null,
      failedShots: ai.failedShots,
      hasLineOfSight: ai.hasLineOfSight,
      targetInCone: ai.targetInCone,
      lastKnown: ai.hasLastKnown ? { x: ai.lastKnownX, z: ai.lastKnownZ } : null,
      spawn: { x: ai.spawnX, z: ai.spawnZ },
      destination: ai.hasDestination ? { x: ai.destinationX, z: ai.destinationZ } : null,
      cover: ai.hasCover ? { x: ai.coverX, z: ai.coverZ } : null
    };
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Agent entity.
   * @returns {THREE.Line}
   * @private
   */
  _ensureDebugMesh(entity) {
    let mesh = this._debugMeshes.get(entity.id);
    if (mesh) return mesh;

    // Enough verts for cone + markers; fixed buffer so we never reallocate.
    const maxVerts = DEBUG_CONE_SEGMENTS + 32;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(maxVerts * 3), 3)
    );
    const material = new THREE.LineBasicMaterial({
      color: DEBUG_COLOR_NEUTRAL,
      depthTest: false,
      transparent: true,
      opacity: 0.85
    });
    mesh = new THREE.Line(geometry, material);
    mesh.frustumCulled = false;
    mesh.renderOrder = 999;
    this.ctx.scene.add(mesh);
    this._debugMeshes.set(entity.id, mesh);
    return mesh;
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Destroyed entity.
   * @returns {void}
   * @private
   */
  _disposeDebugMesh(entity) {
    if (!entity) return;
    this._disposeDebugMeshById(entity.id);
  }

  /**
   * @param {number} id Entity id.
   * @returns {void}
   * @private
   */
  _disposeDebugMeshById(id) {
    const mesh = this._debugMeshes.get(id);
    if (!mesh) return;
    mesh.removeFromParent();
    mesh.geometry.dispose();
    /** @type {THREE.Material} */ (mesh.material).dispose();
    this._debugMeshes.delete(id);
  }

  /** @returns {void} @private */
  _clearAllDebugMeshes() {
    for (const id of [...this._debugMeshes.keys()]) this._disposeDebugMeshById(id);
  }
}
